import axios from 'axios';
import authService from '../services/authService';
import { REACT_APP_BASE_PATH } from '../config/runtimeConfig';
import { parseBackendNotificationDetails } from '../utils/notification';

let errorHandler: ((error: any) => void) | null = null;

export const setGlobalErrorHandler = (handler: (error: any) => void) => {
    errorHandler = handler;
};

export const BASE_PATH = REACT_APP_BASE_PATH || 'https://api.socmanage.com/alert/v1';

const getHttpErrorDetails = (status: number) => {
    switch (status) {
        case 400:
            return {
                title: "Bad Request (400)",
                content: "The request is invalid. Please review the provided data and try again.",
            };
        case 401:
            return {
                title: "Unauthorized (401)",
                content: "Your session is not authorized. Please sign in again.",
            };
        case 403:
            return {
                title: "Forbidden (403)",
                content: "You do not have permission to perform this action.",
            };
        case 404:
            return {
                title: "Not Found (404)",
                content: "The requested resource could not be found.",
            };
        case 409:
            return {
                title: "Conflict (409)",
                content: "The request conflicts with the current state of the resource.",
            };
        case 422:
            return {
                title: "Unprocessable Entity (422)",
                content: "The request data is syntactically correct but semantically invalid.",
            };
        case 429:
            return {
                title: "Too Many Requests (429)",
                content: "Too many requests were sent in a short time. Please try again later.",
            };
        case 500:
            return {
                title: "Internal Server Error (500)",
                content: "The server encountered an unexpected condition.",
            };
        case 502:
            return {
                title: "Bad Gateway (502)",
                content: "The upstream server returned an invalid response.",
            };
        case 503:
            return {
                title: "Service Unavailable (503)",
                content: "The service is temporarily unavailable. Please try again later.",
            };
        case 504:
            return {
                title: "Gateway Timeout (504)",
                content: "The server took too long to respond.",
            };
        default:
            return {
                title: `Request Failed (${status})`,
                content: "The request could not be completed successfully.",
            };
    }
};

const api = axios.create({
    baseURL: BASE_PATH,
    headers: {
        'Content-Type': 'application/json',
    },
});

export const getAccessToken = async (): Promise<string | null> => {
    const user = await authService.getUser();
    if (user) {
        return user.access_token;
    }
    return null;
};

api.interceptors.request.use(
    async (config: any) => {
        const token = await getAccessToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        // Skip global loading if requested
        if (typeof window !== 'undefined' && !(config as any).skipGlobalLoading) {
            window.dispatchEvent(new CustomEvent('loading', { detail: true }));
        }
        return config;
    },
    (error) => {
        if (typeof window !== 'undefined' && !error.config?.skipGlobalLoading) {
            window.dispatchEvent(new CustomEvent('loading', { detail: false }));
        }
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response: any) => {
        if (typeof window !== 'undefined' && !(response.config as any)?.skipGlobalLoading) {
            window.dispatchEvent(new CustomEvent('loading', { detail: false }));
        }
        return response;
    },
    (error: any) => {
        if (typeof window !== 'undefined' && !(error.config as any)?.skipGlobalLoading) {
            window.dispatchEvent(new CustomEvent('loading', { detail: false }));
        }

        if (error.response) {
            const status = error.response.status;

            if (
              status >= 400 &&
              status < 600 &&
              errorHandler != null &&
              !(error.config as any)?.skipGlobalErrorHandler
            ) {
                const details = getHttpErrorDetails(status);
                const backendDetails = parseBackendNotificationDetails(error.response.data);
                const reason = backendDetails.reason || "API Request";
                const content = backendDetails.message || details.content;
                const info = backendDetails.info || `HTTP ${status}`;
                const title =
                    backendDetails.errorCode && backendDetails.reason
                        ? `${backendDetails.reason} (${backendDetails.errorCode})`
                        : details.title;

                errorHandler({
                    status,
                    color: "error",
                    icon: "error",
                    title,
                    reason,
                    content,
                    info,
                    raw: error.response.data,
                });
            }

            if (status === 401) {
                authService.login();
            }

            return Promise.reject(error);
        }

        let message = "An unexpected error occurred while preparing the request.";
        if (error.request) {
            message = "No response was received from the server. Please check your network connection.";
        } else if (error.message) {
            message = `Request setup error: ${error.message}`;
        }

        if (errorHandler != null) {
            errorHandler({
                color: "error",
                icon: "error",
                title: "Network Error",
                reason: "API Request",
                content: message,
                info: "NO_RESPONSE",
            });
        }

        return Promise.reject(error);
    }
);
export const getDataWithConfig = async (endpoint: string, config: any) => {
    try {
        const response = await api.get(endpoint,config);
        return response.data;
    } catch (error) {
        console.error('GET error: %s', error);
        throw error;
    }
};

export const getData = async (endpoint: string, config: any = {}) => {
    try {
        const response = await api.get(endpoint, config);
        return response.data;
    } catch (error) {
        console.error('GET error: %s', error);
        throw error;
    }
};

export const postData = async (endpoint: string, data: any, config: any = {}) => {
    try {
        const response = await api.post(endpoint, data, config);
        return response.data;
    } catch (error: any) {
        console.error('POST error:', error);
        throw error;
    }
};

export const putData = async (endpoint: string, data: any, config: any = {}) => {
    try {
        const response = await api.put(endpoint, data, config);
        return response.data;
    } catch (error) {
        console.error('PUT error: %s', error);
        throw error;
    }
};

export const patchData = async (endpoint: string, data: any, config: any = {}) => {
    try {
        const response = await api.patch(endpoint, data, config);
        return response.data;
    } catch (error) {
        console.error('PATCH error: %s', error);
        throw error;
    }
};

export const deleteData = async (endpoint: string) => {
    try {
        const response = await api.delete(endpoint);
        return response.data;
    } catch (error) {
        console.error('DELETE error: %s', error);
        throw error;
    }
};

export const fileUpload = async (endpoint: string, formData: any, headers: any) => {
    try {
        const response = await api.post(endpoint, formData, headers);
        return response.data;
    } catch (error) {
        console.error('POST error: %s', error);
        throw error;
    }
};

